/**
 * Contract for POST /api/v1/imagegen/image-revamp — AI-generated product
 * photo enhancement/staging for the curator's image-revamp tool.
 *
 * Curator-gated (JWT). Runs against an image ALREADY uploaded to Supabase
 * (see `sourceImage.objectPath` below) and returns the result inline as
 * base64 — nothing is stored server-side. If the curator keeps the result,
 * the APP is responsible for uploading those bytes and persisting
 * `aiGenerated: true` + `generationId` onto the resulting
 * ProductMediaResources entry so the "AI-visualised" disclosure badge shows
 * up later. That save/persist half of the flow is NOT implemented here —
 * this file and AiApiService.imageRevamp only cover generation.
 *
 * Distinct from the three endpoints in ImageAnalyzeObject.ts/AiObject.ts:
 * those turn photos into product DRAFTS (title, category, price, …). This
 * one takes one existing photo and returns an edited/staged version of the
 * same photo — no product fields at all.
 */

// ── Request ──

/** All three default to PRODUCT_1_1_35 (1080x1458), the same 1 : 1.35 shape as the product-image crop. */

/**
 * ENHANCE            photo quality only, background/scene untouched.
 *                     Default format: PRODUCT_1_1_35.
 * VISUALISE           product shown installed / in real-world use context.
 *                     Default format: PRODUCT_1_1_35.
 * CHANGE_BACKGROUND   background replaced, product itself unchanged.
 *                     Default format: PRODUCT_1_1_35.
 */
export type ImageRevampFunctionality =
  | "ENHANCE"
  | "VISUALISE"
  | "CHANGE_BACKGROUND";

/**
 * PORTRAIT_4_5 and SOCIAL_4_5 are both 1080x1350 per the backend spec — not a
 * transcription typo here, that's genuinely two names for one pixel size
 * (presumably two different UI pickers). Pick whichever reads better at the
 * call site; the output is identical either way.
 */
export type ImageRevampFormat =
  | "PRODUCT_1_1_35"
  | "SQUARE_1024"
  | "STORY_9_16"
  | "PORTRAIT_4_5"
  | "LANDSCAPE_16_9"
  | "SOCIAL_4_5";

export interface ImageRevampSourceImage {
  /**
   * Bucket-relative path of an image ALREADY uploaded (e.g. a product draft
   * photo) — this endpoint takes a path, never raw bytes. JPEG/PNG only, max
   * 8MB; WebP is rejected with AI_IMG_UNSUPPORTED_FORMAT (see error codes
   * below). That check happens HERE, at generation time, not at the earlier
   * upload step — so a WebP draft can upload fine and only fail when a
   * curator actually tries to revamp it.
   */
  objectPath: string;
}

/** Optional reference data — stops the model inventing specs. Not validated;
 *  send whatever the product already has. */
export interface ImageRevampProductContext {
  title?: string;
  category?: string;
  details?: Record<string, string>;
}

export interface ImageRevampRequest {
  /** Optional. Audit/quota linkage only — never echoed back in the response,
   *  so `generationId` (response side) is the only id that ties a result
   *  back to anything afterwards. */
  productId?: string;
  sourceImage: ImageRevampSourceImage;
  productContext?: ImageRevampProductContext;
  functionality: ImageRevampFunctionality;
  /** Turbo: deeper, business-aware intelligence. Sent only when on. */
  turbo?: boolean;
  /** Free text, sanitised + capped at 800 chars server-side — enforce
   *  IMAGE_REVAMP_MAX_INSTRUCTIONS_LENGTH client-side too, so a curator finds
   *  out before the round trip instead of after. */
  instructions?: string;
  /** Omit to get the functionality's own default (see
   *  ImageRevampFunctionality above) instead of guessing one client-side. */
  format?: ImageRevampFormat;
}

// ── Response ──

export interface ImageRevampImage {
  /** Always JPEG (~85 quality) regardless of the source format. Render with
   *  `data:image/jpeg;base64,` + base64 — never re-derive the mime type from
   *  the source image. */
  base64: string;
  width: number;
  height: number;
}

/** What the curator chose to keep in the result modal — one flag per photo. */
export interface RevampKeepDecision {
  keepOriginal: boolean;
  keepRevamped: boolean;
}

/** How a kept revamp is saved: "swap" replaces the original in place, "add"
 *  puts the revamp first and keeps the original. */
export type RevampSaveMode = "swap" | "add";

export interface ImageRevampContent {
  /** Written to a server-side audit row (who generated what, when) even
   *  though the image bytes themselves are never stored. Quote it in bug
   *  reports, and pass it through untouched if the result gets saved (see
   *  file header above). */
  generationId: string;
  functionality: ImageRevampFunctionality;
  image: ImageRevampImage;
  /** Always true today — this endpoint has no non-AI output. Still read as a
   *  real field rather than assumed, because it's exactly the value that has
   *  to survive into ProductMediaResources on save for the disclosure badge
   *  to work. */
  aiGenerated: boolean;
  promptVersion: string;
  createdAt: string;
}

export interface ImageRevampGeneralProcessingStatus {
  processingStatus: "OK" | "ERROR";
  processingDetails: string;
}

/** Same envelope ApiErrorHelper.extractApiErrorMessage already reads via
 *  `errorInformation?.[0]?.errorDetail`. */
export interface ImageRevampErrorInformation {
  errorCode: string;
  errorDetail: string;
  additionalDetails: Record<string, any> | null;
}

export interface ImageRevampResponse {
  /** null on success, populated (never empty) on failure. */
  errorInformation: ImageRevampErrorInformation[] | null;
  generalProcessingStatus: ImageRevampGeneralProcessingStatus;
  /** null on failure. Exactly one entry on success today — the backend spec
   *  has no batching for this endpoint despite the array shape. */
  contents: ImageRevampContent[] | null;
}

// ── Error codes ──

/**
 * UNAUTHENTICATED and NOT_CURATOR carry a `BULK_CREATE_*` prefix in the
 * backend spec, not `AI_IMG_*` like every other code on this endpoint. Reads
 * like a copy-paste from the bulk-create auth middleware that never got
 * renamed rather than a deliberate choice — kept verbatim below because it's
 * what the server actually sends. Worth asking backend to fix before this
 * ships broadly; don't "correct" it here or real 401/403s stop matching.
 */
export const IMAGE_REVAMP_ERROR_CODES = {
  VALIDATION_FAILED: "AI_IMG_VALIDATION_FAILED",
  UNSUPPORTED_FORMAT: "AI_IMG_UNSUPPORTED_FORMAT",
  MODERATION_BLOCKED: "AI_IMG_MODERATION_BLOCKED",
  UNAUTHENTICATED: "BULK_CREATE_UNAUTHENTICATED",
  NOT_CURATOR: "BULK_CREATE_NOT_CURATOR",
  QUOTA_EXCEEDED: "AI_IMG_QUOTA_EXCEEDED",
  BUSY: "AI_IMG_BUSY",
  PROVIDER_FAILED: "AI_IMG_PROVIDER_FAILED",
  BUDGET_EXCEEDED: "AI_IMG_BUDGET_EXCEEDED",
} as const;

/**
 * Curator-facing copy per error code, mirroring ANALYZE_ERROR_COPY's pattern
 * in ImageAnalyzeObject.ts. `retryable: false` on MODERATION_BLOCKED is
 * straight from the spec ("never retried") — don't offer a Retry action for
 * that one code. Every other failure is safe to retry.
 */
export const IMAGE_REVAMP_ERROR_COPY: Record<
  string,
  { message: string; retryable: boolean }
> = {
  [IMAGE_REVAMP_ERROR_CODES.VALIDATION_FAILED]: {
    message: "That request was missing something the AI tool needs. Please try again.",
    retryable: true,
  },
  [IMAGE_REVAMP_ERROR_CODES.UNSUPPORTED_FORMAT]: {
    message: "This photo can't be used — convert it to JPEG or PNG and try again.",
    retryable: true,
  },
  [IMAGE_REVAMP_ERROR_CODES.MODERATION_BLOCKED]: {
    message: "This photo or request couldn't be processed. Try a different photo or instructions.",
    retryable: false,
  },
  [IMAGE_REVAMP_ERROR_CODES.UNAUTHENTICATED]: {
    message: "Your session has expired. Please log in again.",
    retryable: false,
  },
  [IMAGE_REVAMP_ERROR_CODES.NOT_CURATOR]: {
    message: "Only curators can use this tool.",
    retryable: false,
  },
  [IMAGE_REVAMP_ERROR_CODES.QUOTA_EXCEEDED]: {
    message: "Daily AI image limit reached. Try again tomorrow.",
    retryable: false,
  },
  [IMAGE_REVAMP_ERROR_CODES.BUSY]: {
    message: "The AI tool is busy right now. Please try again in a moment.",
    retryable: true,
  },
  [IMAGE_REVAMP_ERROR_CODES.PROVIDER_FAILED]: {
    message: "Something went wrong generating that image. Please try again.",
    retryable: true,
  },
  [IMAGE_REVAMP_ERROR_CODES.BUDGET_EXCEEDED]: {
    message: "That took too long to generate. Please try again.",
    retryable: true,
  },
};

/** Falls back to the server's own errorDetail, then to generic copy — an
 *  errorCode this build has never heard of still reads as something. */
export function imageRevampErrorCopy(
  errorCode: string | null | undefined,
  errorDetail?: string | null
): { message: string; retryable: boolean } {
  if (errorCode && IMAGE_REVAMP_ERROR_COPY[errorCode]) {
    return IMAGE_REVAMP_ERROR_COPY[errorCode];
  }
  return {
    message: errorDetail || "Couldn't generate that image. Try again.",
    retryable: true,
  };
}

// ── Client-side limits ──
//
// Mirror server-side limits so a violation is caught before a round trip is
// spent on it — same reasoning as ImageAnalyzeObject.ts's constants.

/** Matches the server-side cap on `instructions`. */
export const IMAGE_REVAMP_MAX_INSTRUCTIONS_LENGTH = 800;
/** Matches the server-side cap on the source image. */
export const IMAGE_REVAMP_MAX_SOURCE_IMAGE_BYTES = 8 * 1024 * 1024;
/** JPEG and PNG only — WebP is rejected, but only at generation time (see
 *  ImageRevampSourceImage above), not at the earlier upload step. */
export const IMAGE_REVAMP_ACCEPTED_SOURCE_MIME_TYPES: readonly string[] = [
  "image/jpeg",
  "image/png",
];
